/**
 * @fileoverview Section "Version" — gouvernance du Design System GNPC.
 *
 * Affiche la version exposée par `@gnpc/ui`, les règles obligatoires de
 * documentation et le catalogue structuré des composants livrés. Cette
 * section sert autant aux designers qu'aux développeurs, aux IDE et aux
 * outils d'analyse du code : elle rend lisible le contrat de maturité du
 * design system.
 */

import {
  DESIGN_SYSTEM_COMPONENTS,
  DESIGN_SYSTEM_DOCUMENTATION_RULES,
  DESIGN_SYSTEM_VERSION,
} from '@gnpc/ui';
import { BadgeCheck, BookOpenCheck, Boxes, CalendarDays } from 'lucide-react';

const STABILITY_LABELS = {
  stable: 'Stable',
  beta: 'Beta',
  alpha: 'Alpha',
  draft: 'Draft',
} as const;

const CATEGORY_LABELS = {
  primitive: 'Primitive',
  form: 'Formulaire',
  feedback: 'Feedback',
  overlay: 'Overlay',
  navigation: 'Navigation',
  data: 'Data',
  utility: 'Utilitaire',
} as const;

export function DsSectionVersion(): JSX.Element {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <div className="border-border bg-card rounded-lg border p-5">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <p className="text-quipux-orange-600 text-sm font-medium">
                {DESIGN_SYSTEM_VERSION.name}
              </p>
              <h3 className="text-quipux-navy-950 mt-1 text-3xl font-semibold tracking-tight">
                v{DESIGN_SYSTEM_VERSION.version}
              </h3>
            </div>
            <span className="border-quipux-orange-200 bg-quipux-orange-50 text-quipux-orange-700 inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium">
              {STABILITY_LABELS[DESIGN_SYSTEM_VERSION.stability]}
            </span>
          </div>
          <p className="text-muted-foreground text-sm leading-6">{DESIGN_SYSTEM_VERSION.summary}</p>
          <div className="text-quipux-navy-800 mt-5 flex flex-wrap gap-3 text-xs">
            <span className="border-border bg-muted inline-flex items-center gap-1.5 rounded-md border px-2 py-1">
              <CalendarDays className="size-3.5" />
              {DESIGN_SYSTEM_VERSION.releasedAt}
            </span>
            <span className="border-border bg-muted inline-flex items-center gap-1.5 rounded-md border px-2 py-1">
              <Boxes className="size-3.5" />
              {DESIGN_SYSTEM_COMPONENTS.length} composants documentés
            </span>
          </div>
        </div>

        <div className="border-border bg-card rounded-lg border p-5">
          <div className="mb-3 flex items-center gap-2">
            <BookOpenCheck className="text-quipux-blue-700 size-4" />
            <h3 className="text-quipux-navy-950 text-sm font-semibold">Contrat IDE / outillage</h3>
          </div>
          <p className="text-muted-foreground text-xs leading-5">
            Les composants publics doivent être compréhensibles depuis l'auto-complétion TypeScript
            sans ouvrir la preview. La vitrine confirme les états visuels, mais le contrat technique
            vit dans les TSDoc et les types exportés.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-quipux-navy-950 text-sm font-semibold">
          Règles de documentation obligatoires
        </h3>
        <div className="grid gap-3 lg:grid-cols-2">
          {DESIGN_SYSTEM_DOCUMENTATION_RULES.map((rule) => (
            <div key={rule.id} className="border-border bg-card rounded-lg border p-4">
              <div className="mb-1 flex items-center gap-2">
                <BadgeCheck className="text-status-paid size-4" />
                <p className="text-quipux-navy-950 text-sm font-medium">{rule.label}</p>
              </div>
              <p className="text-muted-foreground text-xs leading-5">{rule.requirement}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-quipux-navy-950 text-sm font-semibold">
          Composants livrés et documentés
        </h3>
        <div className="border-border bg-card overflow-hidden rounded-lg border">
          <table className="w-full text-left text-sm">
            <thead className="border-border bg-muted/60 text-muted-foreground border-b text-xs uppercase">
              <tr>
                <th className="px-4 py-3 font-semibold">Composant</th>
                <th className="px-4 py-3 font-semibold">Famille</th>
                <th className="px-4 py-3 font-semibold">API clé</th>
                <th className="px-4 py-3 font-semibold">Section</th>
              </tr>
            </thead>
            <tbody className="divide-border divide-y">
              {DESIGN_SYSTEM_COMPONENTS.map((component) => (
                <tr key={component.name}>
                  <td className="px-4 py-3 align-top">
                    <div className="text-quipux-navy-950 font-mono text-xs font-semibold">
                      {component.name}
                    </div>
                    <p className="text-muted-foreground mt-1 max-w-xs text-xs leading-5">
                      {component.purpose}
                    </p>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span className="bg-quipux-blue-50 text-quipux-blue-800 rounded-md px-2 py-1 text-xs font-medium">
                      {CATEGORY_LABELS[component.category]}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex max-w-sm flex-wrap gap-1.5">
                      {component.api.map((api) => (
                        <code
                          key={api}
                          className="bg-muted text-quipux-navy-900 rounded px-1.5 py-0.5 font-mono text-[11px]"
                        >
                          {api}
                        </code>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <a
                      href={`#${component.previewSectionId}`}
                      className="text-quipux-blue-700 hover:text-quipux-blue-800 font-mono text-xs underline underline-offset-2"
                    >
                      #{component.previewSectionId}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
